import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowRight, ShieldCheck, Sparkles, CreditCard, BadgeCheck } from "lucide-react";
import { PHONE_LINK } from "../lib/data";

const LINES = [
  { text: "Grijă pentru", gold: false },
  { text: "fiecare", gold: false },
  { text: "zâmbet.", gold: true },
];

const TRUST = [
  { icon: ShieldCheck, label: "Standard MEDAS din 2019" },
  { icon: Sparkles, label: "Tratament fără durere" },
  { icon: BadgeCheck, label: "Medici cu experiență" },
  { icon: CreditCard, label: "Plată în rate" },
];

const Hero = () => {
  const ref = useRef(null);
  const videoRef = useRef(null);
  const blurRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  useEffect(() => {
    [videoRef.current, blurRef.current].forEach((v) => {
      if (v) {
        v.muted = true;
        v.play().catch(() => {});
      }
    });
  }, []);

  return (
    <section id="acasa" ref={ref} className="relative pt-32 lg:pt-40 pb-16 lg:pb-24 overflow-hidden medical-grid" data-testid="hero-section">
      <div className="absolute top-0 right-0 w-[45%] h-full bg-gradient-to-bl from-[#EFE7D6]/60 to-transparent -z-10" />
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-[2fr_3fr] gap-12 lg:gap-14 items-center">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="inline-flex items-center gap-2 bg-[#EFE7D6] text-[#A6823B] text-xs font-semibold px-3.5 py-1.5 rounded-full mb-7"
            data-testid="hero-badge"
          >
            <span className="w-2 h-2 rounded-full bg-[#A6823B] animate-pulse" />
            Clinică dentară MEDAS Timișoara · din 2019
          </motion.div>

          <h1 data-testid="hero-title" className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#2A2521] leading-[1.06]">
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1 -mb-1">
                <motion.span
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                  className={`block ${line.gold ? "text-[#A6823B]" : ""}`}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.75 }}
            className="mt-6 text-base md:text-lg text-[#6B5C43] leading-relaxed max-w-xl"
          >
            Tratamente dentare fără durere, într-un spațiu cald și primitor.
            Tu vii cu încredere — noi ne ocupăm de rest.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.9 }}
            className="mt-8 flex flex-col sm:flex-row gap-3"
          >
            <a
              href={PHONE_LINK}
              data-testid="hero-phone-button"
              className="group inline-flex items-center justify-center gap-3 bg-[#A6823B] hover:bg-[#8A6B2E] text-white font-semibold text-lg px-7 py-4 rounded-full shadow-xl shadow-[#A6823B]/25 transition-all hover:-translate-y-0.5"
            >
              <span className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center pulse-ring">
                <Phone className="w-4 h-4" />
              </span>
              Sună pentru programare
            </a>
            <Link
              to="/servicii"
              data-testid="hero-services-button"
              className="inline-flex items-center justify-center gap-2 bg-white border border-[#EADFC6] hover:border-[#A6823B] text-[#2A2521] font-semibold text-lg px-7 py-4 rounded-full transition-colors"
            >
              Vezi serviciile <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-10 flex flex-wrap gap-x-6 gap-y-3"
            data-testid="hero-trust-badges"
          >
            {TRUST.map((t) => (
              <div key={t.label} className="flex items-center gap-2 text-sm text-[#6B5C43] font-medium">
                <t.icon className="w-4 h-4 text-[#A6823B]" /> {t.label}
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.9, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="relative"
        >
          <motion.div style={{ y: imgY }} className="relative rounded-3xl overflow-hidden shadow-2xl shadow-[#A6823B]/20 aspect-video bg-[#2A2521]">
            <video
              ref={blurRef}
              src="/videos/hero.mp4"
              autoPlay
              muted
              loop
              playsInline
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover blur-sm scale-110"
            />
            <video
              ref={videoRef}
              src="/videos/hero.mp4"
              poster="/spatii/cabinet2.jpg"
              autoPlay
              muted
              loop
              playsInline
              data-testid="hero-video"
              className="relative w-full h-full object-contain"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2A2521]/25 to-transparent pointer-events-none" />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
